import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Settings, LogOut, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/components/PasswordGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { oldPassword: '', newPassword: '', repeatPassword: '' };

export const SettingsMenu = () => {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const close = () => {
    setOpen(false);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.oldPassword || !form.newPassword || !form.repeatPassword) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    if (form.newPassword.length < 6) {
      toast.error('Das neue Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    if (form.newPassword !== form.repeatPassword) {
      toast.error('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/auth/password`, {
        old_password: form.oldPassword,
        new_password: form.newPassword,
      });
      toast.success('Passwort geändert. Andere Geräte wurden abgemeldet.');
      close();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        toast.error('Altes Passwort ist falsch');
      } else {
        toast.error(err.response?.data?.detail || 'Passwort konnte nicht geändert werden');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        className="border-border text-muted-foreground hover:border-primary hover:text-primary px-2"
        data-testid="settings-button"
        aria-label="Einstellungen"
      >
        <Settings className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <DialogContent className="bg-card border-border mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              Passwort ändern
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label
                htmlFor="old-password"
                className="text-xs font-subheading uppercase tracking-wide text-muted-foreground"
              >
                Altes Passwort
              </Label>
              <Input
                id="old-password"
                data-testid="old-password-input"
                type="password"
                value={form.oldPassword}
                onChange={(e) => setForm({ ...form, oldPassword: e.target.value })}
                className="bg-secondary border-border focus:border-primary h-11"
                autoComplete="current-password"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="new-password"
                className="text-xs font-subheading uppercase tracking-wide text-muted-foreground"
              >
                Neues Passwort
              </Label>
              <Input
                id="new-password"
                data-testid="new-password-input"
                type="password"
                value={form.newPassword}
                onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                className="bg-secondary border-border focus:border-primary h-11"
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">Mindestens 6 Zeichen.</p>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="repeat-password"
                className="text-xs font-subheading uppercase tracking-wide text-muted-foreground"
              >
                Neues Passwort wiederholen
              </Label>
              <Input
                id="repeat-password"
                data-testid="repeat-password-input"
                type="password"
                value={form.repeatPassword}
                onChange={(e) => setForm({ ...form, repeatPassword: e.target.value })}
                className="bg-secondary border-border focus:border-primary h-11"
                autoComplete="new-password"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              onClick={logout}
              variant="outline"
              className="border-border text-muted-foreground hover:border-destructive hover:text-destructive sm:mr-auto"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="save-password-button"
            >
              {saving ? 'Wird gespeichert...' : 'Passwort ändern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
